<template>
  <DeepDialog
    v-model="show"
    :title="t('notifications.settings.title')"
    max-width="500"
    prevent-close-on-click-outside
  >
    <template #content>
      <div v-if="loading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else>
        <p class="text-body-medium mb-3">
          {{ t('notifications.settings.description') }}
        </p>
        <DeepExpandContainer
          v-for="group in localGroups"
          :key="group.key"
          :title="group.label"
          :model-value="true"
          class="notification-settings-expand-container ml-n3"
        >
          <v-checkbox
            v-for="item in group.settings"
            :key="item.event_type"
            :model-value="item.channel === 'email'"
            :label="t(`notifications.settings.event_type.${item.event_type}`)"
            color="primary"
            density="compact"
            hide-details
            @update:model-value="
              (value) => (item.channel = value ? 'email' : 'none')
            "
          />
        </DeepExpandContainer>
      </div>
    </template>
    <template #actions>
      <v-btn-secondary @click="show = false">
        {{ t('actions.cancel') }}
      </v-btn-secondary>
      <v-btn-primary :loading="saveLoading" @click="save">
        {{ t('actions.save') }}
      </v-btn-primary>
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner'
import {
  fetchNotificationSettings,
  updateNotificationSettings,
} from '@/api/notifications'
import { DeepDialog, DeepExpandContainer } from '@deepcloud/deep-ui-lib'
import type { NotificationSettingsGroup } from '@/api/notifications/types'

const { t } = useI18n()

const show = defineModel<boolean>()

const loading = ref(false)
const saveLoading = ref(false)
const localGroups = ref<NotificationSettingsGroup[]>([])

watch(show, async (isOpen) => {
  if (!isOpen) return

  loading.value = true
  try {
    localGroups.value = await fetchNotificationSettings()
  } catch {
    toast.error(t('errors.error_occurred'))
  } finally {
    loading.value = false
  }
})

async function save() {
  saveLoading.value = true
  try {
    const settings = localGroups.value.flatMap((group) => group.settings)
    await updateNotificationSettings(settings)
    toast.success(t('notifications.settings.saved'))
    show.value = false
  } catch {
    toast.error(t('errors.error_occurred'))
  } finally {
    saveLoading.value = false
  }
}
</script>

<style scoped>
.notification-settings-expand-container :deep(.v-list-item__content) {
  min-width: max-content;
}
</style>
